import React from "react";
import { Button } from "primereact/button";
import { Slider, SliderChangeEvent } from "primereact/slider";
import { getSessionStore } from "app/utils/sessionStore";
import { THEME } from "app/utils/types";
import ProjectionsIcon from "app/assets/IconComponent/ProjectionsIcon";
import CalendarIcon from "app/assets/IconComponent/CalendarIcon";
import CandleCake from "app/assets/IconComponent/CandleCake";
import InsuredIcon from "app/assets/IconComponent/InsuredIcon";

interface AssetValueChartFilterProps {
  selectedChartFilter: string;
  onChartFilterChange: (filter: string) => void;
  showProjections: boolean;
  onProjectionsToggle: () => void;
  enableAgeFilter?: boolean;
  enablePlanYearFilter?: boolean;
  enableZoom?: boolean;
  zoomValue?: [number, number];
  zoomMax?: number;
  onZoomChange?: (value: [number, number]) => void;
  hideProjection?: boolean;
  overrideCalendarIcon?: boolean;
}

const AssetValueChartFilter: React.FC<AssetValueChartFilterProps> = ({
  selectedChartFilter,
  onChartFilterChange,
  showProjections,
  onProjectionsToggle,
  enableAgeFilter = true,
  enablePlanYearFilter = true,
  enableZoom = false,
  zoomValue,
  zoomMax,
  onZoomChange,
  overrideCalendarIcon = false,
  hideProjection = false,
}) => {
  const globalTheme = getSessionStore("globalTheme");
  const isDark = globalTheme === THEME.DARK;
  // One button that cycles to the next option on each click - deliberate, per the PO.
  // Page 104 had asked for the options side by side as a segmented control (PR #7328,
  // 29c3ee6f4); the PO reviewed that and asked for the original single cycling icon back,
  // so please do not re-split this without checking with them first. The option list itself
  // is unchanged - Insured Age still appears (and is still gated on enableAgeFilter), it is
  // reached by cycling rather than by its own icon.
  const chartFilterOptions = [
    {
      // IAPP calendar for calendar-year everywhere (the react-icons month grid was the
      // odd one out; overrideCalendarIcon kept for callers but both paths now match).
      renderIcon: (fill: string) => (
        <CalendarIcon width={overrideCalendarIcon ? 30 : 20} fill={fill} />
      ),
      value: "year",
      tooltip: "Year",
    },
    {
      renderIcon: (fill: string) => <CandleCake width={20} height={18} fill={fill} />,
      value: "planYear",
      tooltip: "Plan Year",
    },
    ...(enableAgeFilter
      ? [
          {
            renderIcon: (fill: string) => <InsuredIcon width="22" height="17" fill={fill} />,
            value: "insuredAge",
            tooltip: "Insured Age",
          },
        ]
      : []),
  ].filter((option) => enablePlanYearFilter || option.value !== "planYear");

  const filterIconColor = isDark ? "#fff" : "#00132E";

  const handleChartFilterClick = () => {
    const currentIndex = chartFilterOptions.findIndex((opt) => opt.value === selectedChartFilter);
    const nextIndex = (currentIndex + 1) % chartFilterOptions.length;
    onChartFilterChange(chartFilterOptions[nextIndex].value);
  };

  const currentFilterOption =
    chartFilterOptions.find((opt) => opt.value === selectedChartFilter) || chartFilterOptions[0];

  const projectionsButtonColor = (() => {
    if (showProjections) {
      return isDark ? "#4A90E2" : "#004781";
    }
    return isDark ? "#6B7280" : "#9CA3AF";
  })();

  const shouldShowZoom = enableZoom && typeof zoomMax === "number" && zoomMax > 0 && !!zoomValue;

  const handleZoomChange = (e: SliderChangeEvent) => {
    if (!onZoomChange) return;
    const { value } = e;
    if (!Array.isArray(value) || value.length !== 2) return;
    const [start, end] = value;
    if (typeof start !== "number" || typeof end !== "number") return;
    onZoomChange([start, end]);
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row space-x-2 align-items-center justify-content-between">
        {/* Left side - Chart filter button */}
        <div className="flex flex-row align-items-center gap-2">
          <Button
            icon={currentFilterOption.renderIcon(filterIconColor)}
            text
            size="small"
            className="p-2"
            tooltipOptions={{ position: "top" }}
            tooltip={currentFilterOption.tooltip}
            onClick={handleChartFilterClick}
          />
        </div>

        {/* Right side - Projections toggle */}
        {!hideProjection && (
          <div className="flex flex-row align-items-center gap-2">
            <Button
              icon={
                <ProjectionsIcon
                  key={`projections-icon-${showProjections}`}
                  fill={
                    showProjections
                      ? isDark
                        ? "#4A90E2"
                        : "#004781"
                      : isDark
                      ? "#6B7280"
                      : "#9CA3AF"
                  }
                />
              }
              text
              size="small"
              className="p-2 focus:outline-none focus:ring-0 focus:ring-offset-0 focus:shadow-none"
              severity="secondary"
              tooltipOptions={{ position: "top" }}
              tooltip={showProjections ? "Hide Projections" : "Show Projections"}
              onClick={onProjectionsToggle}
              style={{
                color: projectionsButtonColor,
                outline: "none",
                boxShadow: "none",
              }}
              pt={{
                root: {
                  className: "!shadow-none focus:!shadow-none focus-visible:!shadow-none",
                },
              }}
            />
          </div>
        )}
      </div>

      {shouldShowZoom ? (
        <div className="px-1">
          <Slider
            value={zoomValue}
            min={0}
            max={zoomMax}
            range
            onChange={handleZoomChange}
            pt={{
              range: { className: isDark ? "!bg-[#A7B7CC4D]" : "!bg-[#A7B7CC66]" },
              handle: {
                className: isDark ? "!border-none !bg-white" : "!border-none !bg-[#333333]",
              },
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default React.memo(AssetValueChartFilter);
